"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useWebPushSubscription } from "@/lib/hooks/useWebPushSubscription";
import { isMarketPushReminderEligible } from "@/lib/notifications/market-notification-surface";
import { supabase } from "@/lib/supabase";

export function useMarketPushReminderEligible(nearMePlatformEnabled: boolean) {
  const { user } = useAuth();
  const webPush = useWebPushSubscription();
  const [nearMeOn, setNearMeOn] = useState<boolean | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  useEffect(() => {
    if (!user?.id || !nearMePlatformEnabled) {
      const timer = setTimeout(() => {
        setNearMeOn(null);
        setPrefsLoaded(true);
      }, 0);
      return () => clearTimeout(timer);
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      setPrefsLoaded(false);
      void supabase
        .from("notification_preferences")
        .select("notify_new_listings_near_me")
        .eq("user_id", user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (cancelled) return;
          setNearMeOn(Boolean(data?.notify_new_listings_near_me));
          setPrefsLoaded(true);
        });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [nearMePlatformEnabled, user?.id]);

  const eligible =
    prefsLoaded &&
    isMarketPushReminderEligible({
      user,
      nearMePlatformEnabled,
      nearMeOn,
      browserSubscribed: webPush.browserSubscribed,
      pushPermission: webPush.permission,
      pushSupported: webPush.isSupported,
      pushVapidConfigured: webPush.vapidConfigured,
    });

  return { eligible, prefsLoaded, nearMeOn, webPush };
}
